<template>
	<div class="notify-badges" ref="bar">
		<bottom-badge v-for="ex in extras" :icon="ex.icon" :href="ex.link" :value="ex.value" :color="ex.color"></bottom-badge>
		<bottom-badge v-if="errorMail" icon="outbox" href="viewMail.do?filter=err&folderSwitch=2&showShared=false" :value="errorMail"></bottom-badge>
		<bottom-badge v-if="unreadMail" icon="inbox" href="viewMail.do?filter=unseen&folderSwitch=1&showShared=false" :value="unreadMail"></bottom-badge>
		<bottom-badge v-if="unreadSms" icon="mobile-messages" href="viewSMSs.do?filter=unseen&folderSwitch=1" :value="unreadSms"></bottom-badge>
		<bell-menu :notifications="notifications" @visibility="bellMenuVisibilityChange"></bell-menu>
	</div>
	<Teleport v-if="!notificationsOpen" to="body"><ringing-notifications :list="ringing" @action="doNotificationAction"></ringing-notifications></Teleport>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref, reactive, computed, nextTick } from "vue";
import BottomBadge from "@/ui/mouse/BottomBadge.vue";
import lifecycle from "page-lifecycle";
import {injectEnv} from "@fd/Environment";
import {ExtraPingValue, PingResponse} from "@t/meta/Ping";
import {showAdminMessage} from "@/utils/AdminMessage";
import {Notification, NotificationActionRequest, NotificationActionResponse, NotificationActionResult} from "@t/meta/Notification.ts";
import RingingNotifications from "@/ui/mouse/RingingNotifications.vue";
import {NetworkResponse} from "@fd/Types.ts";
import BellMenu from "@/ui/mouse/BellMenu.vue";
import type {WsRecipient} from "@/ws/WsRecipient.ts";
import ws from "@/ws/UniversalSocket.ts";

const props = defineProps({});
const env = injectEnv();
const emit = defineEmits(["measure"]);	// content changed must be measured
const bar = ref<HTMLDivElement|null>(null);

const PING_RELOAD = 60000;

const errorMail = ref<number|string>(0);
const unreadMail = ref<number|string>(0);
const unreadSms = ref<number|string>(0);
const extras = ref<ExtraPingValue[]>([]);
const notifications = reactive<Notification[]>([]);
const notificationsOpen = ref<boolean>(false);
const ringing = computed<Notification[]>(() => {
	return notifications.filter((n) => n.state === 'ring');
});

let notifyCounter=0;

function getWidth():number {
	if (bar.value) {
		const rect = bar.value.getBoundingClientRect();
		return rect.width;
	}
	return 0;
}

function bellMenuVisibilityChange(visible: boolean) {
	notificationsOpen.value = visible;
}

function processNotifications(nots:Notification[]):void {
	//console.log("Received " + nots.length + " notifications");
	notifications.length = 0;
	for (let n of nots) notifications.push(n);
}

function processNotifyData(ping:PingResponse) {
	//1. adminMessage
	showAdminMessage(ping.adminMessage);

	// 2. show statuses
	errorMail.value = ping.niErrorMailValue > 0 ? ping.niErrorMailValue : 0;
	unreadMail.value = ping.niUnreadMailValue > 0 ? ping.niUnreadMailValue : 0;
	unreadSms.value = ping.niUnreadSmsValue > 0 ? ping.niUnreadSmsValue : 0;
	extras.value.length = 0;
	if (Array.isArray(ping.extras)) {
		for (let ex of ping.extras) extras.value.push(ex);
	}

	// 3. show notification boxes
	processNotifications(Array.isArray(ping.notifications) ? ping.notifications : []);
	nextTick(() => emit("measure"));
}

function updateNotifyInfo(force = false) {
	if (lifecycle.state === "active" || lifecycle.state === "passive" || (notifyCounter%10) === 0 || force) {
		let endpoint = "api/notify";
		if (env.user) {
			endpoint = env.net.urlParam(endpoint, "clientSessionId", env.user.sessionId);
		}
		env.net.fetch(endpoint)
			.then((response) => response.json())
			.then((data) => {
				sessionStorage.setItem("recentNotification", JSON.stringify(data));
				processNotifyData(data);
			})
			.catch((err) => {
				showAdminMessage(env.msg('adminMessage.error.server')+" "+env.msg('adminMessage.error.lostChanges'));
			});
	}
	notifyCounter++;
}

/**
 * Call this if your copy of notification array was notified and it need to be applied into localStorage
 */
function localStoreNotifications() {
	const recent = sessionStorage.getItem("recentNotification");
	if (recent) {
		const olderResponse = JSON.parse(recent) as PingResponse;
		olderResponse.notifications = notifications;
		sessionStorage.setItem("recentNotification", JSON.stringify(olderResponse));
	}
}

function applyActionResult(res: NotificationActionResult) {
	// find notification and its index
	// console.log("  client: apply " + JSON.stringify(res));
	for (let i = 0; i < notifications.length; i++) {
		const nt = notifications[i];
		if (nt.id === res.id) {
			// console.log("  client: found id=" + res.id + " on index " + i);
			if (res.ok) {
				delete nt.error;
				if ("drop" === res.onClient) {
					notifications.splice(i, 1);
					// console.log("  client: removing index "+i+" array is now "+notifications.length+" long")
				} else if ("patch" === res.onClient) {
					// console.log("  client: PATCHING " + i + " with " + JSON.stringify(res.patch));
					if (typeof res.patch === 'object') {
						for (let key of Object.keys(res.patch)) {
							nt[key] = res.patch[key];
						}
						// console.log("  client: patched notification is "+JSON.stringify(nt));
					}
					// Stupid? This was required to force ringing array recalculation
					notifications.length = notifications.length + 1;
					notifications.length = notifications.length - 1;
				}
			} else {
				if ("drop" === res.onClient) {
					notifications.splice(i, 1);	// drop even on error (notification is gone from db)
				} else if (typeof res.error === 'string') nt.error = res.error;
			}
			localStoreNotifications();
			break;
		}
	}
}

/**
 * Do an action on the list of notifications
 * @param name of the action
 * @param notificationIds one or more IDs of notification
 */
function doNotificationAction(name:string, notificationIds:number[]) {
	// console.log("Execute action " + name + " on " + JSON.stringify(notificationIds));
	if (notificationIds.length === 0) return;
	const rq: NotificationActionRequest = {
		ids: notificationIds,
		name: name
	};
	env.net.fetch("api/notify/action", "POST", rq)
		.then((resp:NetworkResponse) => { return resp.json(); })
		.then((results:NotificationActionResponse) => {
			if (results.results) {
				for (let res of results.results) {
					applyActionResult(res);
				}
			}
		});
}


function speedUpNotificationReload() {
	// Cancel original plan
	if (intevalId) clearInterval(intevalId);
	// Call now
	updateNotifyInfo(true);
	// Make a new plan
	intevalId = setInterval(updateNotifyInfo, PING_RELOAD);
}

class NotifyWsRecipient implements WsRecipient {
	constructor() {
	}

	onMessage(sender, message) {
		//console.log("Received notify message from " + sender + ": " + JSON.stringify(message));
		if (typeof message === "object") {
			if ("reload" === message.type) speedUpNotificationReload();
		}
	}
}

let intevalId;
const recipient = new NotifyWsRecipient();

onMounted(() => {
	ws.register("notify", recipient);
	const recent = sessionStorage.getItem("recentNotification");
	if (recent) {
		processNotifyData(JSON.parse(recent) as PingResponse);
	}

	setTimeout(updateNotifyInfo, 500);
	intevalId = setInterval(updateNotifyInfo, PING_RELOAD);
});
onUnmounted(() => {
	ws.unregister("notify");
	if (intevalId) clearInterval(intevalId);
});

defineExpose({
	getWidth
});
</script>

<style>
.notify-badges {
	display: flex;
	flex-direction: row;
	column-gap: 8px;
}
</style>